// @givmo/board — one row of what's coming up or waiting: the home's "Coming up" and a board's own
// "Waiting on you" card (CBG-FIX109), each row this one component.
//
// The house's row (the in-meeting attendance panel's `min-w-0 flex-1`): the words take the row's free
// width and wrap inside it, and the act stays at the row's end. At 1440 the RC10 row's long sentence
// used to push its badge and Open onto a line of their own (design-225's frame h4). On the list that
// holds from the small breakpoint up, and on a phone the act drops beneath the words, as every row's
// did. In a board's card it holds at every width: there the row has one button and no badge (the
// design's by-eye pass: on a phone the longer line's Open had dropped beneath it, the shorter's not).

import type { ReactNode } from 'react';
import { Button, cn } from '@givmo/design-system';

/** A row's one act: "Open" (or the row's own approved word), the list's small secondary button. */
export function ComingUpOpen({
  onClick,
  testId,
  label = 'Open',
  loading = false,
}: {
  onClick: () => void;
  testId: string;
  label?: string;
  loading?: boolean;
}) {
  return (
    <Button
      appearance="saas"
      tone="secondary"
      size="sm"
      loading={loading}
      onClick={onClick}
      testId={testId}
    >
      {label}
    </Button>
  );
}

export interface ComingUpRowProps {
  /** The board's name, on the list. A board's own card names none: the page is the board's. */
  heading?: ReactNode;
  /** What waits, or what's coming. */
  line: ReactNode;
  /** A quiet line beneath it (RC10's "These minutes changed after you signed. …"). */
  note?: ReactNode;
  /** The list's badge. A board's card carries none: its heading says it. */
  badge?: ReactNode;
  /** The row's one act. */
  act: ReactNode;
  /** In a board's "Waiting on you" card. */
  inCard?: boolean;
  testId: string;
}

export function ComingUpRow({
  heading,
  line,
  note,
  badge,
  act,
  inCard = false,
  testId,
}: ComingUpRowProps) {
  return (
    <li
      className={cn(
        'flex items-center justify-between gap-12 border-t border-border-subtle pt-12 first:border-t-0 first:pt-0',
        inCard ? 'flex-nowrap' : 'flex-wrap sm:flex-nowrap',
      )}
      data-testid={testId}
    >
      <div className={cn('flex min-w-0 flex-col gap-2', inCard ? 'flex-1' : 'sm:flex-1')}>
        {heading !== undefined ? (
          <span className="truncate font-sans text-s14 font-w600 tracking-givmo text-text-strong">
            {heading}
          </span>
        ) : null}
        <span
          className={
            inCard
              ? 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text'
              : 'font-sans text-s13 font-w400 tracking-givmo text-text-secondary'
          }
        >
          {line}
        </span>
        {note !== undefined ? (
          <span className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary">
            {note}
          </span>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-10">
        {badge}
        {act}
      </div>
    </li>
  );
}
